import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BarChart3, Cpu, Database, Globe, Layers } from 'lucide-react';
import { baseUrlWithAuth } from '../../../hooks/remote/apiClient';

// Pass profileData from StudentProfile; on Resources this component fetches it.
const AssignedResourceQuotas = ({ profileData }) => {
  const userSessionData = useSelector((state) => state.user.userSessionData);
  const [fetchedProfile, setFetchedProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const sessionProfile = userSessionData?.student_profile || userSessionData?.student || userSessionData?.user?.student || userSessionData;
  const schoolId = userSessionData?.school?.id || sessionProfile?.school?.id || userSessionData?.school_id || sessionProfile?.school_id;
  const studentId = userSessionData?.school?.student_id || sessionProfile?.school?.student_id || userSessionData?.student_id || sessionProfile?.student_id || userSessionData?.student?.id || userSessionData?.student_profile?.id;

  useEffect(() => {
    if (profileData !== undefined) return;
    let cancelled = false;
    if (!schoolId || !studentId) {
      setError(true);
      return;
    }
    setLoading(true);
    setError(false);
    baseUrlWithAuth.get(`/school/${schoolId}/students/${studentId}/`)
      .then((response) => { if (!cancelled) setFetchedProfile(response.data?.data || response.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [profileData, schoolId, studentId]);

  const profile = profileData !== undefined ? profileData : fetchedProfile;
  const quota = profile?.resource_usage || profile?.resource_quota || {};
  const items = [
    { label: 'Virtual Servers (Dews)', used: quota.servers_used ?? quota.used_servers ?? quota.dews_used ?? quota.instances_used ?? 0, limit: quota.servers_limit ?? quota.max_servers ?? quota.dews_limit ?? quota.instances_limit ?? 0, icon: Cpu, color: 'bg-indigo-500', iconColor: 'text-indigo-500' },
    { label: 'Volumes', used: quota.volumes_used ?? quota.used_volumes ?? 0, limit: quota.volumes_limit ?? quota.max_volumes ?? 0, icon: Database, color: 'bg-emerald-500', iconColor: 'text-emerald-500' },
    { label: 'Private Networks', used: quota.networks_used ?? quota.used_networks ?? 0, limit: quota.networks_limit ?? quota.max_networks ?? 0, icon: Globe, color: 'bg-blue-500', iconColor: 'text-blue-500' },
    { label: 'Load Balancers', used: quota.load_balancers_used ?? quota.used_load_balancers ?? 0, limit: quota.load_balancers_limit ?? quota.max_load_balancers ?? 0, icon: Layers, color: 'bg-amber-500', iconColor: 'text-amber-500' },
    { label: 'Storage Space', used: quota.storage_used_gb ?? quota.used_storage_gb ?? quota.storage_used ?? 0, limit: quota.storage_limit_gb ?? quota.max_storage_gb ?? quota.storage_limit ?? 0, unit: 'GB', icon: Database, color: 'bg-rose-500', iconColor: 'text-rose-500' },
    { label: 'Server Snapshots', used: quota.server_snapshots_used ?? quota.used_server_snapshots ?? 0, limit: quota.server_snapshots_limit ?? quota.max_server_snapshots ?? 0, icon: Cpu, color: 'bg-violet-500', iconColor: 'text-violet-500' },
    { label: 'Volume Snapshots', used: quota.volume_snapshots_used ?? quota.used_volume_snapshots ?? 0, limit: quota.volume_snapshots_limit ?? quota.max_volume_snapshots ?? 0, icon: Database, color: 'bg-teal-500', iconColor: 'text-teal-500' },
    { label: 'Database Instances', used: quota.db_instances_used ?? quota.database_instances_used ?? quota.used_databases ?? 0, limit: quota.db_instances_limit ?? quota.database_instances_limit ?? quota.max_databases ?? 0, icon: Database, color: 'bg-cyan-500', iconColor: 'text-cyan-500' },
  ];

  return (
    <section className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200/80 dark:border-[#c4c4c416] p-6 shadow-sm mb-8">
      <h3 className="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-primaryBlue" /> Assigned Resource Quotas
      </h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-6">
        Track your cloud usage against quotas allocated by your school's usage template.
      </p>
      {loading ? <p className="text-xs text-gray-500">Loading resource quotas...</p> : error ? (
        <p className="text-xs text-amber-600">Unable to load assigned resource quotas. Please try refreshing the page.</p>
      ) : Object.keys(quota).length === 0 ? (
        <p className="text-xs text-amber-600">No quota data found from the student profile response.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          {items.map((item) => {
            const used = Number(item.used) || 0;
            const limit = Number(item.limit) || 0;
            const percent = limit > 0 ? Math.min(Math.max(Math.round(used / limit * 100), 0), 100) : 0;
            const Icon = item.icon;
            return (
              <div key={item.label} className="space-y-2">
                <div className="flex justify-between items-center gap-2 text-xs">
                  <span className="font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2"><Icon className={`w-4 h-4 shrink-0 ${item.iconColor}`} />{item.label}</span>
                  <span className="font-bold text-gray-600 dark:text-gray-400 whitespace-nowrap">{used}{item.unit || ''} / {limit}{item.unit || ''} ({percent}%)</span>
                </div>
                <div className="w-full bg-gray-100 dark:bg-gray-700 h-2.5 rounded-full overflow-hidden">
                  <div className={`${item.color} h-full rounded-full transition-all duration-500`} style={{ width: `${percent}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default AssignedResourceQuotas;
